import type { ExploreUserEventRow } from '@services/userEventsExploreService';
import type { Event as DatasetEvent } from '@store/unifiedDataStore';
import {
  parseDatasetEventTags,
  noteTextFromParsedTags,
  tagThumbsFromTags,
} from '@utils/datasetEventTags';

export type TagExploreMapMarkerCategory =
  | 'comment'
  | 'flag'
  | 'maneuver-up'
  | 'maneuver-down'
  | 'media';

export interface TagExploreMapMarker {
  id: string;
  /** ISO timestamp for track interpolation */
  atIso: string;
  category: TagExploreMapMarkerCategory;
  tooltip: string;
}

const MANEUVER_TYPES = new Set(['tack', 'gybe', 'roundup', 'bearaway']);

function isManeuver(ev: DatasetEvent): boolean {
  return MANEUVER_TYPES.has((ev.event_type || '').toLowerCase());
}

/** Interpolation time on the track: API focus_time (maneuver midpoint in explore), else local midpoint, else start. */
function datasetManeuverTrackTimeIso(ev: DatasetEvent): string | null {
  const focus = ev.focus_time;
  if (focus != null && String(focus).trim() !== '') {
    const tf = new Date(focus);
    if (!Number.isNaN(tf.getTime())) {return tf.toISOString();}
  }
  const st = ev.start_time;
  const en = ev.end_time;
  if (st != null && String(st).trim() !== '' && en != null && String(en).trim() !== '') {
    const ta = new Date(st).getTime();
    const tb = new Date(en).getTime();
    if (!Number.isNaN(ta) && !Number.isNaN(tb)) {
      return new Date((ta + tb) / 2).toISOString();
    }
  }
  if (st == null || String(st).trim() === '') {return null;}
  const t = new Date(st);
  return Number.isNaN(t.getTime()) ? null : t.toISOString();
}

function userEventTimeIso(row: ExploreUserEventRow): string | null {
  const iso = row.focus_time || row.start_time;
  if (iso == null || String(iso).trim() === '') {return null;}
  const t = new Date(iso);
  return Number.isNaN(t.getTime()) ? null : t.toISOString();
}

export type TagExploreMergedRowInput =
  | { kind: 'user_event'; user: ExploreUserEventRow }
  | { kind: 'dataset_event'; ev: DatasetEvent }
  | {
      kind: 'media';
      id: string;
      mediaSource: string;
      start: Date;
      end: Date;
    };

function mediaClipMidpointIso(start: Date, end: Date): string | null {
  const ta = start.getTime();
  const tb = end.getTime();
  if (!Number.isFinite(ta) || !Number.isFinite(tb)) {return null;}
  return new Date((ta + tb) / 2).toISOString();
}

/**
 * Map panel rows to markers for the explore map overlay (COMMENT/FLAG, maneuver thumbs, short MEDIA clips).
 */
export function buildTagExploreMapMarkers(rows: TagExploreMergedRowInput[]): TagExploreMapMarker[] {
  const out: TagExploreMapMarker[] = [];
  for (const row of rows) {
    if (row.kind === 'media') {
      const atIso = mediaClipMidpointIso(row.start, row.end);
      if (!atIso) {continue;}
      const src = (row.mediaSource || '').trim() || 'MEDIA';
      const startMs = row.start.getTime();
      out.push({
        id: `media:${row.id}:${startMs}`,
        atIso,
        category: 'media',
        tooltip: src,
      });
      continue;
    }
    if (row.kind === 'user_event') {
      const u = row.user;
      const et = (u.event_type || '').toUpperCase();
      if (et !== 'COMMENT' && et !== 'FLAG') {continue;}
      const atIso = userEventTimeIso(u);
      if (!atIso) {continue;}
      const tip = (u.comment || '').trim() || et;
      out.push({
        id: `ue:${u.user_event_id}:${atIso}`,
        atIso,
        category: et === 'FLAG' ? 'flag' : 'comment',
        tooltip: tip,
      });
      continue;
    }
    const ev = row.ev;
    if (!isManeuver(ev)) {continue;}
    const parsedTags = parseDatasetEventTags(ev.tags);
    const thumbs = tagThumbsFromTags(parsedTags);
    if (thumbs !== true && thumbs !== false) {continue;}
    const atIso = datasetManeuverTrackTimeIso(ev);
    if (!atIso) {continue;}
    const note = noteTextFromParsedTags(parsedTags).trim();
    const et = (ev.event_type || '').trim() || 'Maneuver';
    const tip = note ? `${note}\n${et}` : et;
    out.push({
      id: `de:${ev.event_id}:${atIso}`,
      atIso,
      category: thumbs ? 'maneuver-up' : 'maneuver-down',
      tooltip: tip,
    });
  }
  return out;
}
